const CATEGORIES = [
  {
    key: 'gedolim',
    href: '/s/art/gedolim',
    title: 'Gedolim portraits',
    description:
      'Portraits of Rabbanim and Gedolim, created in a range of styles from traditional to more contemporary interpretations.',
  },
  {
    key: 'the_classics',
    href: '/s/art/the_classics',
    title: 'Jerusalem & Kotel art',
    description:
      'Artwork centered on Jerusalem, the Kotel, and scenes from Israel, from classic depictions to more modern interpretations.',
  },
  {
    key: 'commission',
    href: '/artist/custom_co_studio',
    title: 'Commission a Painting',
    description:
      'Custom artwork created by an artist based on your request, whether for a specific subject, size, or occasion.',
  },
  {
    key: 'jewish_icons',
    href: '/s/art/jewish_icons',
    title: 'Jewish Life & Tradition Art',
    description:
      'Artwork reflecting Jewish life, tradition, and everyday moments, from symbolic pieces to more narrative scenes.',
  },
  {
    key: 'around_the_home',
    href: '/s/art/around_the_home',
    title: 'Jewish Art for the Home',
    description:
      'Artwork for everyday spaces, including nature, children\'s pieces, and non-Judaica designs that still feel at home in a Jewish setting.',
  },
];

const getSchema = rootURL => {
  const artURL = `${rootURL}/s/art`;
  return [
    {
      '@type': 'CollectionPage',
      '@id': `${rootURL}/s/art#webpage`,
      url: artURL,
      name: 'Jewish Art & Paintings — From Independent Jewish Artists',
      description:
        'Jewish art and original paintings, prints, and commissioned work by independent Jewish artists — from Jerusalem & Kotel Art to Gedolim Portraits.',
      isPartOf: { '@id': `${rootURL}/#website` },
      about: [
        { '@type': 'Thing', name: 'Jewish Art' },
        { '@type': 'Thing', name: 'Original Paintings' },
        { '@type': 'Thing', name: 'Art Prints' },
        { '@type': 'Thing', name: 'Judaica Canvas Art' },
        { '@type': 'Thing', name: 'Jerusalem & Kotel Art' },
        { '@type': 'Thing', name: 'Gedolim Portraits' },
      ],
      publisher: { '@id': `${rootURL}/#organization` },
      mainEntity: { '@id': `${rootURL}/s/art#categories` },
    },
    {
      '@type': 'ItemList',
      '@id': `${rootURL}/s/art#categories`,
      name: 'Jewish Art Categories',
      itemListElement: CATEGORIES.map((cat, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: cat.title,
        url: `${rootURL}${cat.href}`,
      })),
    },
    {
      '@type': 'FAQPage',
      '@id': `${rootURL}/s/art#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is considered Jewish art?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Jewish art refers to work that reflects Jewish life, history, or tradition. That can be a Jerusalem or Kotel scene, a Gedolim portrait, Hebrew text, or something more abstract that still fits naturally in a Jewish home. Some pieces are traditional and familiar. Others are more modern in style, but still rooted in Jewish themes.',
          },
        },
        {
          '@type': 'Question',
          name: 'What types of Jewish art are available?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most Jewish art comes in a few formats: Original paintings, Prints, Mixed media, Commissioned pieces.',
          },
        },
        {
          '@type': 'Question',
          name: 'What are common themes in Jewish art?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Certain themes show up repeatedly because they're familiar in Jewish homes: Jerusalem & Kotel Art, Gedolim Portraits, Hebrew text and calligraphy, Scenes of Jewish life and tradition. Most buyers start with something familiar, then branch out from there.",
          },
        },
        {
          '@type': 'Question',
          name: 'What is the difference between an original painting and a print?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Original paintings are one-of-a-kind pieces created by the artist. Prints are reproductions of an original work at a lower price. Mixed media sits in between — often a print with hand-applied elements or layered materials that add depth. Most homes use prints or mixed media for everyday spaces, while originals are usually chosen when someone wants a more central or meaningful piece.',
          },
        },
        {
          '@type': 'Question',
          name: 'What should I look for when choosing Jewish art?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Most people know where the couch goes and hesitate on what goes above it. A simple way to start: Theme first — Jerusalem, text, portraits, or something more abstract. Then size — make sure it fits the wall properly. Then format — original, print, or mixed media depending on budget. As a rough guide, pieces that feel large in a store usually look right at home. Most living room walls comfortably hold a 60–90cm wide piece.",
          },
        },
        {
          '@type': 'Question',
          name: 'Can I commission a custom Jewish painting or Gedolim portrait?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, in many cases. Commissioning is common if you want a specific size or subject, are buying for a meaningful occasion, or have a particular style in mind. You can usually work directly with the artist to create something personal. You can also browse the commissions hub for custom work.',
          },
        },
        {
          '@type': 'Question',
          name: 'Where can I buy Jewish art?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Jewish art is sold in a few different ways: Directly from individual artists, Through marketplaces that bring multiple artists together, On large general platforms with a wide range of styles and quality. Joodaic brings independent Jewish artists into one focused catalog, so you can browse different styles and themes in one place.',
          },
        },
      ],
    },
  ];
};

const artSeoConfig = {
  key: 'art',
  match: ({ listingType }) => listingType === 'art',
  accentColor: '--colorArt',
  links: CATEGORIES,
  title: 'Jewish Art & Paintings — From Independent Jewish Artists',
  description:
    'Jewish art and original paintings, prints, and commissioned work by independent Jewish artists — from Jerusalem & Kotel Art to Gedolim Portraits.',
  getSchema,
  intro: {
    heading: 'Jewish Art & Paintings',
    tagline:
      'Jewish art and original paintings, prints, and commissioned work by independent Jewish artists — from Jerusalem & Kotel Art to Gedolim Portraits.',
    collapsedHeading: 'Jewish Art & Paintings',
    bodyParagraphs: [
      'Joodaic is a marketplace for Jewish art created by independent artists. The collection includes original paintings, prints, and mixed media across categories like Jerusalem & Kotel Art, Gedolim Portraits, Jewish Life & Tradition Art, and Around the Home.',
    ],
    linksLabel: 'Explore Jewish Art Categories',
  },
  bottomSections: [
    {
      id: 'intro',
      type: 'prose',
      heading: 'Jewish Art by Independent Artists',
      paragraphs: [
        'Joodaic is a marketplace for Jewish art — paintings, prints, and commissioned work by independent Jewish artists.',
        'The collection includes Jerusalem & Kotel Art, Gedolim Portraits, and work across Jewish Life & Tradition Art and Around the Home, with styles ranging from traditional to more contemporary interpretations.',
        'Instead of a single aesthetic, the catalog brings together many different artistic approaches, all rooted in Jewish themes and designed for the home.',
      ],
    },
    {
      id: 'canvas',
      title: 'Judaica Canvas Art',
      paragraphs: [
        'Judaica canvas art offers ready-to-hang pieces that create a strong visual presence on the wall. These are often used for larger works, bold color, and pieces that anchor a living room or dining space.',
      ],
    },
    {
      id: 'paintings',
      title: 'Original Paintings',
      paragraphs: [
        'Judaica paintings include original works created by artists exploring Jewish life, Jerusalem scenes, and Gedolim Portraits. These are often chosen as more central pieces or meaningful long-term additions to the home.',
      ],
    },
    {
      id: 'prints',
      title: 'Prints and Reproductions',
      paragraphs: [
        'Jewish artwork prints make it easier to bring meaningful Jewish art into everyday spaces at a lower price point. Many are reproductions of original works, sometimes offered as limited editions or with hand-finished details.',
      ],
    },
    {
      id: 'styles',
      title: 'Different Styles of Jewish Art',
      paragraphs: [
        'Jewish art covers a wide range of styles. Some pieces fall into Jerusalem & Kotel Art or Gedolim Portraits, while others explore themes from Jewish Life & Tradition Art or Around the Home.',
        'You can browse different artistic directions using the filters above to find work that fits your home.',
      ],
      paragraphsAfter: [
        'For Jerusalem art that centres the landscape and place — Kotel scenes, Old City views, Kever Rachel, and more — browse <a href="/s/art/the_classics">Jerusalem &amp; Kotel art</a>.',
      ],
    },
  ],
  faqHeading: 'About Jewish Art',
  faq: [
    {
      question: 'What is considered Jewish art?',
      answer: [
        'Jewish art refers to work that reflects Jewish life, history, or tradition. That can be a Jerusalem or Kotel scene, a Gedolim portrait, Hebrew text, or something more abstract that still fits naturally in a Jewish home.',
        'Some pieces are traditional and familiar. Others are more modern in style, but still rooted in Jewish themes.',
      ],
    },
    {
      question: 'What types of Jewish art are available?',
      answer: [
        'Most Jewish art comes in a few formats:',
        ['Original paintings', 'Prints', 'Mixed media', 'Commissioned pieces'],
      ],
    },
    {
      question: 'What are common themes in Jewish art?',
      answer: [
        "Certain themes show up repeatedly because they're familiar in Jewish homes:",
        [
          'Jerusalem & Kotel Art',
          'Gedolim Portraits',
          'Hebrew text and calligraphy',
          'Scenes of Jewish life and tradition',
        ],
        'Most buyers start with something familiar, then branch out from there.',
      ],
    },
    {
      question: 'What is the difference between an original painting and a print?',
      answer: [
        [
          'Original paintings are one-of-a-kind pieces created by the artist',
          'Prints are reproductions of an original work at a lower price',
          'Mixed media sits in between — often a print with hand-applied elements or layered materials that add depth',
        ],
        'Most homes use prints or mixed media for everyday spaces, while originals are usually chosen when someone wants a more central or meaningful piece.',
      ],
    },
    {
      question: 'What should I look for when choosing Jewish art?',
      answer: [
        'Most people know where the couch goes and hesitate on what goes above it.',
        'A simple way to start:',
        [
          'Theme first — Jerusalem, text, portraits, or something more abstract',
          'Then size — make sure it fits the wall properly',
          'Then format — original, print, or mixed media depending on budget',
        ],
        'As a rough guide, pieces that feel large in a store usually look right at home. Most living room walls comfortably hold a 60–90cm wide piece.',
        "If you're unsure, you can upload a photo of your wall and preview different pieces in place to see what works before committing.",
      ],
    },
    {
      question: 'Can I commission a custom Jewish painting or Gedolim portrait?',
      answer: [
        'Yes, in many cases.',
        'Commissioning is common if you:',
        [
          'want a specific size or subject',
          'are buying for a meaningful occasion',
          'or have a particular style in mind',
        ],
        'You can usually work directly with the artist to create something personal.',
        'You can also browse the <a href="/artist/custom_co_studio">commissions hub</a> for custom work.',
      ],
    },
    {
      question: 'Where can I buy Jewish art?',
      answer: [
        'Jewish art is sold in a few different ways:',
        [
          'Directly from individual artists',
          'Through marketplaces that bring multiple artists together',
          'On large general platforms with a wide range of styles and quality',
        ],
        'Joodaic brings independent Jewish artists into one focused catalog, so you can browse different styles and themes in one place.',
      ],
    },
  ],
};

export default artSeoConfig;
